import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ServerErrorPage } from '@/pages/ServerErrorPage';

/**
 * Tenta extrair um identificador opaco de falha de qualquer erro lançado
 * dentro da árvore React. Funciona com erros Axios via duck typing — sem
 * importar axios para manter este componente leve e desacoplado.
 *
 * Procura, em ordem:
 *   1. `err.response.headers['x-correlation-id']`
 *   2. `err.response.data.correlation_id`
 *   3. `err.correlationId` (já em formato camelCase)
 */
export function extractCorrelationId(err: unknown): string | null {
  if (err == null || typeof err !== 'object') return null;
  const anyErr = err as {
    response?: { headers?: Record<string, unknown>; data?: Record<string, unknown> };
    correlationId?: unknown;
  };
  const fromHeader = anyErr.response?.headers?.['x-correlation-id'];
  if (typeof fromHeader === 'string' && fromHeader.length > 0) return fromHeader;
  const fromBody = anyErr.response?.data?.correlation_id;
  if (typeof fromBody === 'string' && fromBody.length > 0) return fromBody;
  if (typeof anyErr.correlationId === 'string' && anyErr.correlationId.length > 0) {
    return anyErr.correlationId;
  }
  return null;
}

interface ErrorBoundaryState {
  hasError: boolean;
  correlationId: string | null;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Hook opcional para telemetria/logging. Disparado em `componentDidCatch`.
   * Não exibe nada na UI; cabe ao consumidor decidir destino.
   */
  onCaught?: (error: unknown, info: ErrorInfo) => void;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false, correlationId: null };

  public static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, correlationId: extractCorrelationId(error) };
  }

  public componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.onCaught?.(error, info);
  }

  private readonly handleReset = (): void => {
    this.setState({ hasError: false, correlationId: null });
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ServerErrorPage correlationId={this.state.correlationId} onRetry={this.handleReset} />
      );
    }
    return this.props.children;
  }
}
